"use client"
import React from "react";
import { useResizeHook } from "../../../lib";
import { CategoryDetails, CategoryPoint, SubCategory, Category } from "../../../types";
import ContentTitles from "../../typography/content-titles/ContentTitles";

type CategoryHighlightsContentTypes = {
    category: CategoryDetails & Category;
    showPoints?: boolean;
    hideTitle?: boolean;
    hideSubtitle?: boolean;
    hideServicesLinks?: boolean;
    alternateBackground?: boolean;
    showMainCategoryLink?: boolean;
    overlineTitle?: string;
};

const ServicesHighlightsContent = ({
                                       category,
                                       hideSubtitle,
                                       showPoints,
                                       hideTitle,
                                       hideServicesLinks,
                                       alternateBackground,
                                       showMainCategoryLink,
                                       overlineTitle
                                   }: CategoryHighlightsContentTypes) => {
    const { greaterThanTailwindMd768 } = useResizeHook();

    const mainColor =
        alternateBackground || !greaterThanTailwindMd768
            ? "text-blue-600 border-blue-600"
            : "text-secondary border-secondary";

    const serviceButton = `capitalize border rounded-xl bg-white px-4 py-2 text-center ${mainColor}`;
    const mainCategoryButton =
        "capitalize mt-3 rounded-xl bg-white text-black border border-gray-300 px-4 py-2 hover:bg-white";

    const renderPointIcon = (icon: CategoryPoint["icon"]) => {
        if (!icon) return null;
        if (React.isValidElement(icon)) return icon;

        const Icon = icon as React.ElementType;
        return <Icon />;
    };

    return (
        <div className="flex flex-col justify-center items-center py-12 px-6 lg:px-6 lg:py-4">

            {(!hideTitle || !!overlineTitle?.length) && (
                <div className="pb-10 md:pb-5">
                    <ContentTitles overline={overlineTitle} title={hideTitle ? undefined : category.name} />
                </div>
            )}

            {/* Mobile / Tablet Gallery */}
            {!greaterThanTailwindMd768 && category.images?.length ? (
                <div className="w-full flex justify-center mb-6">
                    <img
                        src={category.images[0].original}
                        alt={category.images[0].originalAlt || category.images[0].title || ""}
                        className="w-full rounded"
                    />
                </div>
            ) : null}

            {/* Subtitle */}
            <ContentTitles
                subTitle={
                    hideSubtitle
                        ? ""
                        : Array.isArray(category.description)
                            ? category.description.join(", ")
                            : category.description || ""
                }
            />

            {/* Subcategory Buttons */}
            {!hideServicesLinks && category.subCategories?.length ? (
                <div className="flex flex-wrap justify-center gap-3 pt-6 lg:pb-2">
                    {category.subCategories.map((sc: SubCategory) => (
                        <a
                            key={sc.id}
                            href={`/services/${category.slug}/${sc.slug}`}
                            className={serviceButton}
                        >
                            {sc.title}
                        </a>
                    ))}
                </div>
            ) : null}

            {/* Points */}
            {showPoints && category.points?.length ? (
                <div className="w-full pt-12 pb-6 md:py-6">
                    {category.points.map((point: CategoryPoint, index) => (
                        <div
                            key={index}
                            className="flex items-center justify-center pb-2 md:items-start"
                        >
                            {point.icon ? <span>{renderPointIcon(point.icon)}</span> : null}
                            <div className="ml-2">
                                <h6 className="text-sm md:py-0 md:my-0 text-left">{point.title}</h6>
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}

            {/* Main Category Button */}
            {showMainCategoryLink && (
                <a
                    href={`/services/${category.slug}`}
                    className={mainCategoryButton}
                >
                    {category.name}
                </a>
            )}
        </div>
    );
};

export default ServicesHighlightsContent;
